import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { VerticalID, VerticalManifest } from '../types/vertical';
import { errorMessage } from '../lib/errors';
import { BRAND_ACCENT, resolveAccent } from '../lib/brandAccent';

// Default Fallback (Generic/Mechanic hybrid for safety)
const DEFAULT_MANIFEST: VerticalManifest = {
    id: 'generic',
    jargon: {
        entity_label: 'Vehicle',
        client_label: 'Customer',
        trade_label: 'Business',
        service_label: 'Service',
        asset_label: 'Vehicle',
        action_label: 'Job',
        primary_action: 'New Job',
        asset_identifier: 'ID',
        item_label: 'Item',
        sidebar: {
            dashboard: 'Dashboard',
            workshop: 'Workshop',
            clients: 'Customers',
            expenses: 'Expenses',
            intelligence: 'Financials',
            copilot: 'Co-pilot',
            settings: 'Settings'
        }
    },
    sidebar_config: [
        { label: 'Dashboard', path: '/', icon: 'LayoutDashboard', permission: 'dashboard' },
        { label: 'Workshop', path: '/workshop', icon: 'Wrench', permission: 'workshop' },
        { label: 'Clients', path: '/clients', icon: 'Users', permission: 'workshop' },
        { label: 'Invoices', path: '/invoices', icon: 'FileText', permission: 'workshop' },
        { label: 'Expenses', path: '/expenses', icon: 'Receipt', permission: 'expenses' },
        // Supplier balances are money the business owes — an owner/accountant
        // concern, not a mechanic's. `financial_reports` is owner+admin, which
        // matches fn_can_view_financials, the function that already gates the
        // supplier_accounts table in RLS. It was 'workshop' here, so mechanics
        // were shown a menu item leading to a page the database then emptied.
        { label: 'Supplier Accounts', path: '/returns', icon: 'Landmark', permission: 'financial_reports' },
        { label: 'Business Health', path: '/business-health', icon: 'BarChart3', permission: 'financial_reports' },
        { label: 'Settings', path: '/settings', icon: 'Settings', permission: 'settings' }
    ],
    ui: {
        primary_accent: BRAND_ACCENT,
        mood: 'industrial',
        radius: 'md',
    },
    features: {
        has_vehicle_lookup: false,
        has_scheduling: true,
        has_inventory: true,
    },
};

const RADIUS: Record<string, string> = { full: '9999px', lg: '14px', md: '10px' };

const applyUiTokens = (ui: VerticalManifest['ui']) => {
    // The RAW accent: each theme shades it for contrast (styles/theme.css derives
    // --fds-accent from it). Setting --fds-accent itself would bypass that.
    document.documentElement.style.setProperty('--fd-accent-raw', resolveAccent(ui.primary_accent));
    document.documentElement.style.setProperty('--fds-radius', RADIUS[ui.radius] ?? RADIUS.md);
};

export const useMoldedUI = () => {
    const getCachedManifest = (): VerticalManifest => {
        try {
            const cached = localStorage.getItem('fds_vertical_manifest_v2');
            if (cached) return JSON.parse(cached);
        } catch {
            // Ignore parse errors
        }
        return DEFAULT_MANIFEST;
    };

    const [molded, setMolded] = useState<VerticalManifest>(getCachedManifest());
    const [activeOrgId, setActiveOrgId] = useState<string | null>(null);
    const [orgCreatedAt, setOrgCreatedAt] = useState<string | null>(null);
    const [loading, setLoading] = useState(molded.id === 'generic'); // Only show loading if no active cache
    const [error, setError] = useState<string | null>(null);

    // Bootstrap CSS vars on initial load from cache
    useEffect(() => {
        applyUiTokens(molded.ui);
    }, []);

    useEffect(() => {
        const fetchMolding = async () => {
            try {
                // We keep UI responsive, no need to show global loading state if we have cache

                // 1. Get User
                const { data: { user } } = await supabase.auth.getUser();

                if (!user) {
                    setLoading(false);
                    return;
                }

                // 2. Get Profile to find Active Org
                const { data: profile, error: profileError } = await supabase
                    .from('profiles')
                    .select('active_org_id')
                    .eq('id', user.id)
                    .single();

                if (profileError) throw profileError;

                let verticalId: VerticalID = 'mechanic'; // Default fallback
                let fetchedOrgCreatedAt: string | null = null;

                if (profile?.active_org_id) {
                    setActiveOrgId(profile.active_org_id);
                    // 3. Get Organization to find Vertical ID and created_at
                    const { data: org, error: orgError } = await supabase
                        .from('organizations')
                        .select('vertical_id, created_at')
                        .eq('id', profile.active_org_id)
                        .single();

                    if (!orgError && org) {
                        if (org.vertical_id) verticalId = org.vertical_id as VerticalID;
                        if (org.created_at) fetchedOrgCreatedAt = org.created_at;
                    }
                }
                
                setOrgCreatedAt(fetchedOrgCreatedAt);

                // 4. Fetch Vertical Manifest from Registry
                const { data: verticalData, error: verticalError } = await supabase
                    .from('vertical_registry')
                    .select('*')
                    .eq('id', verticalId)
                    .single();

                if (verticalError) throw verticalError;

                if (verticalData) {
                    const dbTokens = verticalData.ui_tokens || {};
                    const newManifest: VerticalManifest = {
                        id: verticalData.id as VerticalID,
                        jargon: verticalData.jargon || DEFAULT_MANIFEST.jargon,
                        ui: {
                            ...DEFAULT_MANIFEST.ui,
                            ...dbTokens,
                            // Map DB 'primary_color' to 'primary_accent' if missing
                            primary_accent: dbTokens.primary_accent || dbTokens.primary_color || DEFAULT_MANIFEST.ui.primary_accent
                        },
                        features: verticalData.features || DEFAULT_MANIFEST.features,
                        sidebar_config: verticalData.sidebar_config || DEFAULT_MANIFEST.sidebar_config
                    };

                    setMolded(newManifest);
                    
                    // Save to cache for offline/instant load
                    localStorage.setItem('fds_vertical_manifest_v2', JSON.stringify(newManifest));

                    // 5. Inject CSS Variables
                    applyUiTokens(newManifest.ui);
                }
            } catch (err) {
                console.error('Molding Error:', err);
                setError(errorMessage(err));
                // Keep default manifest on error
            } finally {
                setLoading(false);
            }
        };

        fetchMolding();
    }, []);

    return { molded, activeOrgId, orgCreatedAt, loading, error };
};
